























import type {
  Comment,
  CommentMargins,
} from '@getaccept/lib-shared/src/contextual-comments/types/comment';
import type { PropType, Ref } from '@vue/composition-api';
import { defineComponent, ref, toRefs, watch } from '@vue/composition-api';
import Vue from 'vue';
import type { Recipient } from '../../types/Recipient';
import type { User } from '../../users/types/user';
import { useCommentsHeightsCalculator } from '../composables/comments-heights-calculator';
import { FloatingCommentsHelper } from '../helpers/floating-comments.helper';
import type { CurrentUser } from '../types/current-user';

import ConversationCard from './ConversationCard.vue';

export default defineComponent({
  name: 'FloatingComments',
  components: {
    ConversationCard,
  },
  props: {
    comments: {
      type: Array as PropType<Comment[]>,
      default: () => [],
    },
    currentUser: {
      type: Object as PropType<CurrentUser>,
      required: true,
    },
    selectedId: {
      type: String,
      default: '',
    },
    dimensionsUpdating: {
      type: Boolean,
      default: false,
    },
    recipients: {
      type: Array as PropType<Recipient[]>,
      required: true,
    },
    sender: {
      type: Object as PropType<User>,
      required: true,
    },
  },

  setup(props, { emit }) {
    const commentsMargins: Ref<CommentMargins> = ref({});

    const { commentsHeights, resetCommentsHeights, addHeight } = useCommentsHeightsCalculator();

    const reset = () => {
      commentsMargins.value = {};
      resetCommentsHeights();
    };

    const { comments, dimensionsUpdating, selectedId } = toRefs(props);

    watch(
      () => comments.value.length,
      () => {
        reset();
      }
    );
    const clickOutside = () => {
      emit('select', '');
    };

    watch(dimensionsUpdating, updating => {
      if (!updating) {
        reset();
      }
    });

    const marginOffset = 16; // We shouldn't calculate the offset like this

    const calculateMarkTagDistanceToTop = (
      dataCommentId: string,
      currentPageDistanceToTop: number
    ): number => {
      const commentMarkTag: HTMLElement = document.querySelector(
        `[data-comment-id="${dataCommentId}"]`
      );
      if (!commentMarkTag) {
        return 0;
      }
      const commentMarkDistanceToTop = commentMarkTag.getBoundingClientRect().top;
      return commentMarkDistanceToTop - currentPageDistanceToTop - marginOffset;
    };

    const minimumMargin = 10;

    const setFirstCommentMargins = (commentId: string, margin: number) => {
      Vue.set(commentsMargins.value, commentId, {
        desired: margin,
        actual: margin,
        selection: margin,
      });
    };

    const setMinimumMargins = (commentId: string, desired: number) => {
      Vue.set(commentsMargins.value, commentId, {
        desired,
        actual: minimumMargin,
        selection: minimumMargin,
      });
    };

    const setNewMargins = (commentId: string, newMargin: number, desired: number) => {
      Vue.set(commentsMargins.value, commentId, {
        desired,
        actual: newMargin,
        selection: newMargin,
      });
    };

    const setMargins = (commentsContainerTopPosition: number) => {
      let totalHeight = 0;
      comments.value.forEach((comment: Comment, index: number) => {
        const distanceToTop = calculateMarkTagDistanceToTop(
          comment.id,
          commentsContainerTopPosition
        );

        if (index === 0) {
          setFirstCommentMargins(comment.id, distanceToTop);
          totalHeight = distanceToTop + commentsHeights.value[comment.id];
          return;
        }

        const newMargin = distanceToTop - totalHeight;

        if (newMargin < minimumMargin) {
          setMinimumMargins(comment.id, distanceToTop);
          totalHeight += minimumMargin + commentsHeights.value[comment.id];
          return;
        }
        setNewMargins(comment.id, newMargin, distanceToTop);
        totalHeight += newMargin + commentsHeights.value[comment.id];
      }, 0);
    };

    const commentsContainer: Ref<HTMLElement> = ref(null);

    watch(commentsHeights, value => {
      if (Object.keys(value).length !== comments.value.length) {
        return;
      }
      setMargins(commentsContainer.value.getBoundingClientRect().top);
      if (selectedId.value) {
        adjustMarginsDuringSelection();
      }
    });

    const resetCommentsSelectionMargins = () =>
      Object.keys(commentsMargins.value).forEach(
        (id: string) => (commentsMargins.value[id].selection = commentsMargins.value[id].actual)
      );

    const getActualDistanceFromTop = (commentIds: string[], actual: number) =>
      commentIds.reduce(
        (distance: number, id: string) =>
          distance + commentsHeights.value[id] + commentsMargins.value[id].actual,
        0
      ) + actual;

    const getSelectionDistanceFromTop = (commentIds: string[], selection: number) =>
      commentIds.reduce(
        (distance: number, id: string) =>
          distance + commentsHeights.value[id] + commentsMargins.value[id].selection,
        0
      ) + selection;

    const increaseMarginForFollowingComments = (commentId: string, marginToAdd: number) => {
      const commentIds = FloatingCommentsHelper.getCommentIdsAfterSelectedComment(
        commentId,
        comments.value
      );
      if (!commentIds.length) {
        return;
      }
      commentIds.forEach((id: string) => {
        const previousCommentIds = FloatingCommentsHelper.getReversedCommentIdsBeforeId(
          id,
          comments.value
        );
        const combinedDistance = getSelectionDistanceFromTop(
          previousCommentIds,
          commentsMargins.value[id].actual
        );
        const futureDistance = combinedDistance - marginToAdd;

        if (futureDistance === commentsMargins.value[id].desired) {
          return;
        }
        if (
          FloatingCommentsHelper.willCommentBePushedDown(
            futureDistance,
            commentsMargins.value[id].desired
          )
        ) {
          return;
        }
        const marginIncrease = commentsMargins.value[id].desired - futureDistance;
        commentsMargins.value[id].selection += marginIncrease;
      });
    };

    const adjustMarginsDuringSelection = () => {
      resetCommentsSelectionMargins();
      const { desired, actual } = commentsMargins.value[selectedId.value];

      if (desired === actual) {
        commentsMargins.value[selectedId.value].selection = desired;
        return;
      }

      const previousCommentIds = FloatingCommentsHelper.getReversedCommentIdsBeforeId(
        selectedId.value,
        comments.value
      );

      const distanceFromTop = getActualDistanceFromTop(previousCommentIds, actual);
      const distanceToRemove = distanceFromTop - desired;

      increaseMarginForFollowingComments(selectedId.value, distanceToRemove);

      if (previousCommentIds.length === 0) {
        return;
      }

      let distanceRemaining = distanceToRemove;
      previousCommentIds.forEach((id: string, index: number) => {
        if (distanceRemaining === 0) {
          return;
        }

        if (FloatingCommentsHelper.evaluateIsIndexFinalComment(index, previousCommentIds.length)) {
          commentsMargins.value[id].selection -= distanceRemaining;
          distanceRemaining = 0;
          return;
        }

        if (commentsMargins.value[id].actual === minimumMargin) {
          return;
        }

        const allowedMarginToRemove = FloatingCommentsHelper.getMarginToRemoveFromComment(
          commentsMargins.value[id].actual,
          distanceRemaining,
          minimumMargin
        );

        if (allowedMarginToRemove === 0) {
          return;
        }

        commentsMargins.value[id].selection -= allowedMarginToRemove;
        distanceRemaining -= allowedMarginToRemove;
      });
    };

    watch(selectedId, (id: string) => {
      if (!id) {
        return;
      }
      adjustMarginsDuringSelection();
    });

    const getMarginTop = (commentId: string) => {
      if (!commentsMargins.value[commentId]) {
        return 0;
      }
      if (selectedId.value) {
        return FloatingCommentsHelper.evaluateMarginOptions(
          commentsMargins.value[commentId].actual,
          commentsMargins.value[commentId].selection
        );
      }
      return commentsMargins.value[commentId].actual;
    };

    return {
      addHeight,
      commentsContainer,
      getMarginTop,
      clickOutside,
      // Exposed for tests
      commentsHeights,
      commentsMargins,
      minimumMargin,
      marginOffset,
      reset,
      setFirstCommentMargins,
      setMinimumMargins,
      setNewMargins,
      resetCommentsSelectionMargins,
      getActualDistanceFromTop,
      getSelectionDistanceFromTop,
      setMargins,
      calculateMarkTagDistanceToTop,
      increaseMarginForFollowingComments,
      adjustMarginsDuringSelection,
    };
  },
});
